import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export default function LoginPage() {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // バリデーション
    if (!formData.email || !formData.password) {
      setError('メールアドレスとパスワードを入力してください');
      setIsSubmitting(false);
      return;
    }

    if (!isLogin && !formData.name) {
      setError('お名前を入力してください');
      setIsSubmitting(false);
      return;
    }

    // TODO: 実際の認証処理はSupabase接続後に実装
    // 現在はモック処理としてroleに応じた遷移を実装
    
    try {
      // シミュレーション用の遅延
      await new Promise(resolve => setTimeout(resolve, 800));

      // モックのrole判定（実際はSupabaseから取得）
      // デモ用：email に応じて role を分岐
      let userRole = 'owner'; // デフォルトはオーナー
      
      if (formData.email.includes('sitter')) {
        userRole = 'sitter';
      } else if (formData.email.includes('pending')) {
        userRole = 'pending_sitter';
      }

      // roleに応じて遷移先を決定
      switch (userRole) {
        case 'owner':
          navigate('/dashboard/owner');
          break;
        case 'sitter':
          navigate('/dashboard/sitter');
          break;
        case 'pending_sitter':
          navigate('/sitter-application-status');
          break;
        default:
          navigate('/role-select');
      }
    } catch (err) {
      setError('ログインに失敗しました。もう一度お試しください。');
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    // エラーをクリア
    if (error) setError('');
  };

  return (
    <div className="min-h-screen bg-[#EEF4F7] flex flex-col">
      {/* Navigation */}
      <nav className="bg-[#81D8D0] shadow-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between h-24 sm:h-28">
            <Link to="/" className="flex items-center flex-shrink-0">
              <img 
                src="https://static.readdy.ai/image/8518bd20f704ac7d2f3f65f4644eeb9a/37cbdfbab98dc394d9ee285d8ad1648c.svg" 
                alt="PawBridge" 
                className="w-40 h-40 sm:w-48 sm:h-48 object-contain brightness-0"
              />
            </Link>
            
            <Link 
              to="/"
              className="flex items-center gap-2 text-base font-medium text-white/90 hover:text-white transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line text-lg"></i>
              ホームへ戻る
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* Card */}
          <div className="bg-white rounded-3xl shadow-xl p-8 sm:p-10">
            {/* Logo & Title */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#81D8D0] rounded-full flex items-center justify-center">
                <i className="ri-user-heart-line text-3xl text-white"></i>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-[#1B2A4A] mb-2">
                {isLogin ? 'ログイン' : '新規登録'}
              </h1>
              <p className="text-sm text-[#1B2A4A]/60">
                PawBridgeへようこそ
              </p>
            </div>

            {/* Tab Switcher */}
            <div className="flex gap-2 mb-6 bg-[#EEF4F7] p-1 rounded-full">
              <button
                type="button"
                onClick={() => {
                  setIsLogin(true);
                  setError('');
                }}
                className={`flex-1 py-2.5 text-sm font-semibold rounded-full transition-all whitespace-nowrap cursor-pointer ${
                  isLogin
                    ? 'bg-[#81D8D0] text-white shadow-sm'
                    : 'text-[#1B2A4A]/60 hover:text-[#1B2A4A]'
                }`}
              >
                ログイン
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsLogin(false);
                  setError('');
                }}
                className={`flex-1 py-2.5 text-sm font-semibold rounded-full transition-all whitespace-nowrap cursor-pointer ${
                  !isLogin
                    ? 'bg-[#81D8D0] text-white shadow-sm'
                    : 'text-[#1B2A4A]/60 hover:text-[#1B2A4A]'
                }`}
              >
                新規登録
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                <i className="ri-error-warning-line text-red-500 text-lg flex-shrink-0 mt-0.5"></i>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {!isLogin && (
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-[#1B2A4A] mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                      <i className="ri-user-line text-lg text-[#81D8D0]"></i>
                    </div>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required={!isLogin}
                      placeholder="山田太郎"
                      className="w-full pl-12 pr-4 py-3.5 text-sm border-2 border-[#D4E8EC] rounded-xl focus:border-[#81D8D0] focus:outline-none transition-colors"
                    />
                  </div>
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-[#1B2A4A] mb-2">
                  メールアドレス <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                    <i className="ri-mail-line text-lg text-[#81D8D0]"></i>
                  </div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="example@email.com"
                    className="w-full pl-12 pr-4 py-3.5 text-sm border-2 border-[#D4E8EC] rounded-xl focus:border-[#81D8D0] focus:outline-none transition-colors"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-[#1B2A4A] mb-2">
                  パスワード <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                    <i className="ri-lock-line text-lg text-[#81D8D0]"></i>
                  </div>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength={6}
                    placeholder="••••••••"
                    className="w-full pl-12 pr-4 py-3.5 text-sm border-2 border-[#D4E8EC] rounded-xl focus:border-[#81D8D0] focus:outline-none transition-colors"
                  />
                </div>
                {!isLogin && (
                  <p className="mt-2 text-xs text-[#1B2A4A]/60">
                    6文字以上で入力してください
                  </p>
                )}
              </div>

              {isLogin && (
                <div className="flex items-center justify-between text-sm">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded border-[#D4E8EC] text-[#81D8D0] focus:ring-[#81D8D0] cursor-pointer"
                    />
                    <span className="text-[#1B2A4A]/70">ログイン状態を保持</span>
                  </label>
                  <button
                    type="button"
                    className="text-[#81D8D0] hover:text-[#5ec9bf] font-medium transition-colors cursor-pointer"
                  >
                    パスワードを忘れた
                  </button>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-[#81D8D0] text-white text-base font-bold rounded-xl hover:bg-[#5ec9bf] transition-all shadow-lg shadow-[#81D8D0]/30 hover:shadow-xl hover:shadow-[#81D8D0]/40 hover:-translate-y-0.5 disabled:bg-[#D4E8EC] disabled:cursor-not-allowed disabled:shadow-none disabled:transform-none whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line text-xl animate-spin"></i>
                    処理中...
                  </span>
                ) : (
                  isLogin ? 'ログイン' : '新規登録'
                )}
              </button>
            </form>

            {/* Demo Info */}
            {isLogin && (
              <div className="mt-6 p-4 bg-[#EEF4F7] rounded-xl border border-[#D4E8EC]">
                <div className="flex items-start gap-3">
                  <i className="ri-information-line text-[#81D8D0] text-lg flex-shrink-0 mt-0.5"></i>
                  <div className="text-xs text-[#1B2A4A]/70 leading-relaxed">
                    <p className="font-semibold text-[#81D8D0] mb-2">デモ用アカウント：</p>
                    <p className="mb-1">• <strong>オーナー：</strong> 任意のメールアドレス</p>
                    <p className="mb-1">• <strong>シッター：</strong> sitter@example.com</p>
                    <p>• <strong>審査中：</strong> pending@example.com</p>
                  </div>
                </div>
              </div>
            )}

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#D4E8EC]"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-4 bg-white text-[#1B2A4A]/50">または</span>
              </div>
            </div>

            {/* Social Login */}
            <div className="space-y-3">
              <button
                type="button"
                className="w-full py-3.5 border-2 border-[#D4E8EC] text-[#1B2A4A] text-sm font-semibold rounded-xl hover:border-[#81D8D0] hover:bg-[#EEF4F7] transition-all flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-google-fill text-xl text-red-500"></i>
                Googleで続ける
              </button>
              <button
                type="button"
                className="w-full py-3.5 border-2 border-[#D4E8EC] text-[#1B2A4A] text-sm font-semibold rounded-xl hover:border-[#81D8D0] hover:bg-[#EEF4F7] transition-all flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-facebook-fill text-xl text-blue-600"></i>
                Facebookで続ける
              </button>
            </div>

            {/* Terms */}
            {!isLogin && (
              <p className="mt-6 text-xs text-center text-[#1B2A4A]/60 leading-relaxed">
                登録することで、PawBridgeの
                <Link to="/terms" className="text-[#81D8D0] hover:underline">利用規約</Link>
                および
                <Link to="/terms" className="text-[#81D8D0] hover:underline">プライバシーポリシー</Link>
                に同意したものとみなされます
              </p>
            )}
          </div>

          {/* Help Text */}
          <p className="mt-6 text-center text-sm text-[#1B2A4A]/60">
            お困りですか？
            <button
              type="button"
              onClick={() => {
                // TODO: ヘルプセンターへの遷移
              }}
              className="text-[#81D8D0] hover:text-[#5ec9bf] font-medium ml-1 transition-colors cursor-pointer"
            >
              ヘルプセンター
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}